import type { ApiResponse, CrmAction, CrmUser, NamedItem } from '@/models/api'
import { api, readData } from '@/services/apiClient'
import { loadReferenceData } from '@/services/referenceService'

export const actionPageSize = 20

export interface ActionQuery {
  assignedToId?: string
  customerId?: string
  includeClosed?: boolean
  actionTypes?: string[]
  categories?: number[]
  showFor?: 'All' | 'Overdue' | 'Today'
  search?: string
  page?: number
  pageSize?: number
}

export interface ActionPage {
  rows: CrmAction[]
  total: number
}

export interface SaveActionInput {
  id?: string
  customerId: string
  assignedToId: string
  actionType: string
  actionDetail: string
  actionDate: string
}

export async function loadActions(query: ActionQuery): Promise<ActionPage> {
  const params = new URLSearchParams()
  if (query.assignedToId) params.set('assignedToId', query.assignedToId)
  if (query.customerId) params.set('customerId', query.customerId)
  if (query.includeClosed) params.set('includeClosed', 'true')
  for (const type of query.actionTypes ?? []) params.append('actionTypes', type)
  for (const category of query.categories ?? []) params.append('categories', String(category))
  params.set('showFor', query.showFor ?? 'All')
  const search = query.search?.trim()
  if (search) params.set('search', search)
  params.set('page', String(query.page ?? 1))
  params.set('pageSize', String(query.pageSize ?? actionPageSize))
  const { data } = await api.get<ApiResponse<CrmAction[]>>(`/actions?${params.toString()}`)
  const rows = readData(data, [])
  const parsed = typeof data.extraData === 'number' ? data.extraData : Number(data.extraData)
  return { rows, total: Number.isFinite(parsed) ? parsed : rows.length }
}

export async function saveAction(input: SaveActionInput): Promise<string> {
  const { data } = await api.post<ApiResponse<string>>('/actions', {
    id: input.id || null,
    customerId: input.customerId,
    assignedToId: input.assignedToId,
    actionType: input.actionType,
    actionDetail: input.actionDetail,
    actionDate: input.actionDate,
  })
  return readData(data, '')
}

export async function deleteAction(actionId: string): Promise<void> {
  const { data } = await api.delete<ApiResponse<null>>(`/actions/${actionId}`)
  readData(data, null)
}

export async function closeAction(actionId: string, closeDetail: string, followUp?: SaveActionInput): Promise<void> {
  const { data } = await api.post<ApiResponse<null>>(`/actions/${actionId}/close`, {
    actionId,
    closeDetail: closeDetail || null,
    followUp,
  })
  readData(data, null)
}

export async function reassignAction(actionId: string, assignedToId: string): Promise<void> {
  const { data } = await api.post<ApiResponse<null>>(`/actions/${actionId}/reassign`, {
    actionId,
    assignedToId,
  })
  readData(data, null)
}

export async function rescheduleActions(actionIds: string[], actionDate: string): Promise<void> {
  const { data } = await api.post<ApiResponse<null>>('/actions/reschedule', { actionIds, actionDate })
  readData(data, null)
}

export async function loadAssignableUsers(): Promise<CrmUser[]> {
  const { data } = await api.get<ApiResponse<CrmUser[]>>('/users', {
    params: { enabledOnly: true, excludeReadOnly: true },
  })
  return readData(data, [])
}

export async function loadCustomerCategories(): Promise<NamedItem[]> {
  const items = await loadReferenceData('customer-categories')
  return items.filter((item) => item.enabled)
}

export async function reopenAction(id: string): Promise<void> {
  const { data } = await api.post<ApiResponse<null>>(`/actions/${id}/reopen`)
  readData(data, null)
}
